import {
  Stack,
  Box,
  Typography,
  IconButton,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { Fragment, useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDButton from "components/MDButton";
import Props from "./types";
import useData from "./hooks/useData";

const btnStyle = {
  border: "1px solid #7b809a",
  width: "100%",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 1,
};

const orderButtonSx = {
  "&:disabled": {
    opacity: 0.6,
  },
};

function SelectMultiple<itemGql>({
  data,
  handleChange,
  path,
  label,
  required,
  errors,
  Modal,
  requestItem,
  formatItem,
  enabled,
  uischema,
}: Props<itemGql>) {
  const [open, setOpen] = useState(false);
  const { listItems, setListItems, loading } = useData<itemGql>({
    ids: data,
    requestItem,
    formatItem,
  });

  useEffect(() => {
    if (data?.length && data.length >= (uischema?.options?.max ?? Infinity)) {
      setOpen(false);
    }
    if (
      (data !== undefined && !Array.isArray(data)) ||
      data?.some((item) => typeof item !== "number" && typeof item !== "string")
    ) {
      throw new Error("Invalid param, must be an Array of number or string");
    }
  }, [data]);

  const handleAddItem = ({ id, name }: { name: string; id: number }) => {
    if (!id || !name || !enabled) return;

    handleChange(path, [...(data || []), id]);
    const newData = [...listItems, { id, name }];
    setListItems(newData);
  };

  const handleRemoveItem = ({ id, name }: { name: string; id: number }) => {
    if (!id || !name || !enabled) return;

    const newData = data?.filter((item) => item !== id);
    handleChange(path, newData);
  };

  const onChange = ({ id, name }: { name: string; id: number }) => {
    if (!id || !name || !enabled) {
      console.log(id, name);
      return;
    }

    const remove = (data as any)?.includes(id);
    if (remove) {
      handleRemoveItem({ id, name });
    } else {
      handleAddItem({ id, name });
    }
  };

  const handleOrder = (orderType: "up" | "down", index: number) => {
    if (index < 0 || index >= listItems.length) {
      return;
    }

    const updatedItems = [...listItems];

    if (orderType === "up" && index > 0) {
      [updatedItems[index - 1], updatedItems[index]] = [
        updatedItems[index],
        updatedItems[index - 1],
      ];
    } else if (orderType === "down" && index < listItems.length - 1) {
      [updatedItems[index], updatedItems[index + 1]] = [
        updatedItems[index + 1],
        updatedItems[index],
      ];
    } else {
      return;
    }

    setListItems(updatedItems);
    handleChange(
      path,
      updatedItems.map((item) => item.id)
    );
  };

  const handleOpen = useCallback(() => setOpen((old) => (enabled ? !old : false)), []);

  if (loading) {
    return (
      <Stack alignItems={"center"}>
        <CircularProgress sx={{ color: "#7b809a" }} />
      </Stack>
    );
  }

  return (
    <Fragment>
      <Stack gap={1} margin={"10px 0"}>
        <Box
          component={"p"}
          sx={{
            fontFamily: "Roboto",
            fontSize: "0.9rem",
            fontWeight: "400",
            lineHeight: "1.4375em",
            letterSpacing: "0.00938em",
            padding: 0,
            position: "relative",
            color: !!errors ? "#F44335" : "#7b809a",
          }}
        >
          {label}
          {required && " *"}
        </Box>
        <Stack>
          {data?.map((id, index) => (
            <Stack
              key={id}
              direction={"row"}
              alignItems={"center"}
              width={"100%"}
              justifyContent={"space-between"}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  color: "#344767",
                  fontSize: "1rem",
                }}
              >
                {listItems?.find((data) => data.id === id)?.name}
              </Typography>
              <Stack alignItems={"center"} direction={"row"} gap={"6px"}>
                {uischema?.options?.itemPath && (
                  <IconButton
                    aria-label="Abrir cadastro em nova aba"
                    size="medium"
                    onClick={() =>
                      window.open(uischema.options.itemPath.replace(":id", String(id)), "_blank", "noopener,noreferrer")
                    }
                  >
                    <OpenInNewIcon />
                  </IconButton>
                )}
                <IconButton
                  disabled={!enabled || index === listItems.length - 1}
                  size="medium"
                  onClick={() => handleOrder("down", index)}
                  sx={orderButtonSx}
                >
                  <ArrowDownwardIcon />
                </IconButton>
                <IconButton
                  disabled={!enabled || index === 0}
                  size="medium"
                  onClick={() => handleOrder("up", index)}
                  sx={orderButtonSx}
                >
                  <ArrowUpwardIcon />
                </IconButton>

                <IconButton
                  disabled={!enabled}
                  aria-label="delete"
                  size="medium"
                  onClick={() => onChange(listItems?.find((data) => data.id === id))}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
        <MDButton
          sx={btnStyle}
          onClick={handleOpen}
          disabled={
            !enabled || (data?.length && data?.length >= (uischema?.options?.max ?? Infinity))
          }
        >
          Adicionar
          <AddIcon />
        </MDButton>
      </Stack>
      {!!errors && <FormHelperText error>{errors}</FormHelperText>}
      {enabled && (
        <Modal open={open} ids={data ?? []} onChange={onChange} handleOpen={handleOpen} />
      )}
    </Fragment>
  );
}

export default SelectMultiple;
